import { PageContainer, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCollegeProfileQuery } from "@/hooks/api/college.queries";
import {
  Building2,
  Calendar,
  Globe,
  GraduationCap,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Shield,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ─── Info Row helper ─────────────────────────────────────────────────────────

const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
}) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-400">{label}</p>
        <p className="break-words text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
};

// ─── Profile View ─────────────────────────────────────────────────────────────

const Profile = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useCollegeProfileQuery();

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex h-[400px] items-center justify-center text-sm text-gray-400">
        No profile data found.
      </div>
    );
  }

  // Initials avatar
  const initials = (profile.name || "C")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <PageContainer className="space-y-6 pb-10">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-500">Settings</span>
          <h1 className="text-2xl font-bold">College Profile</h1>
        </div>
        <Button
          id="edit-profile-btn"
          onClick={() => navigate("/profile/edit")}
          className="flex items-center gap-2 self-start sm:self-auto"
        >
          <Pencil size={15} />
          Edit Profile
        </Button>
      </div>

      {/* Hero Card */}
      <Card className="overflow-hidden border-gray-100 shadow-sm">
        {/* Gradient banner */}
        <div className="h-24 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500" />

        <CardContent className="relative px-6 pb-6 pt-0">
          {/* Avatar */}
          <div className="flex items-end gap-5">
            <div className="-mt-10 flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-4 border-white bg-blue-600 text-2xl font-bold text-white shadow-md">
              {initials}
            </div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">
                {profile.name}
              </h2>
              {profile.status && (
                <Badge
                  className={`rounded-full px-3 capitalize ${
                    profile.status === "verified"
                      ? "bg-green-100 text-green-700 hover:bg-green-100"
                      : "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                  }`}
                >
                  {profile.status}
                </Badge>
              )}
              {profile.mou_signed && (
                <Badge className="rounded-full bg-blue-100 px-3 text-blue-700 hover:bg-blue-100">
                  MOU Signed
                </Badge>
              )}
            </div>
          </div>

          {/* Description */}
          {profile.description && (
            <p className="mt-4 text-sm leading-relaxed text-gray-500">
              {profile.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Contact Information */}
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              icon={<Mail size={16} />}
              label="Email"
              value={profile.email}
            />
            <InfoRow
              icon={<Phone size={16} />}
              label="Phone"
              value={profile.phone}
            />
            <InfoRow
              icon={<MapPin size={16} />}
              label="Address"
              value={profile.address}
            />
            <InfoRow
              icon={<Globe size={16} />}
              label="Website"
              value={profile.website}
            />
          </CardContent>
        </Card>

        {/* Academic Details */}
        <Card className="border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">
              Academic Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              icon={<Shield size={16} />}
              label="Accreditation"
              value={profile.accreditation}
            />
            <InfoRow
              icon={<Calendar size={16} />}
              label="Established Year"
              value={profile.established_year}
            />
            <InfoRow
              icon={<Users size={16} />}
              label="Total Students"
              value={
                profile.total_students
                  ? profile.total_students.toLocaleString()
                  : null
              }
            />
            <InfoRow
              icon={<GraduationCap size={16} />}
              label="Status"
              value={profile.status}
            />
            <InfoRow
              icon={<Building2 size={16} />}
              label="MOU Signed"
              value={profile.mou_signed ? "Yes" : "No"}
            />
          </CardContent>
        </Card>
      </div>

      {/* Timestamps */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-400">
        <span>
          Created:{" "}
          <span className="text-gray-500">
            {new Date(profile.created_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </span>
        <span>•</span>
        <span>
          Last Updated:{" "}
          <span className="text-gray-500">
            {new Date(profile.updated_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </span>
      </div>
    </PageContainer>
  );
};

export default Profile;
